// RecurrencePreviewLine — small helper line under the recurrence
// inputs in the add modal. Shows the user, before they commit, how
// many occurrences the current rule will generate over the chosen
// date range. Intended to head off "wait, why did 200 rows just
// land?" after Save.
//
// The count comes from `countRecurrenceOccurrences`, the pure
// frontend mirror of backend `recurrenceService.generateOccurrences`.
// Sharing the math (rather than re-rolling per-frequency arithmetic
// here) keeps the on-screen preview honest at every boundary case
// the backend handles — anchor-day clamping, inclusive endpoints,
// the 104-occurrence safety cap, and interval > 1 stepping.
//
// Renders nothing for invalid or zero-range inputs so the line
// doesn't shout "0 monthly occurrences" while the user is still
// typing dates.

import type { RecurrenceFrequency } from "../../../../types";
import { countRecurrenceOccurrences } from "./recurrenceCount";

export interface RecurrencePreviewLineProps {
  frequency: RecurrenceFrequency;
  interval: number;
  startDate: string;
  endDate: string;
}

const FREQ_LABEL: Record<RecurrenceFrequency, string> = {
  daily: "daily",
  weekly: "weekly",
  monthly: "monthly",
  quarterly: "quarterly",
  annually: "annual",
};

function formatMonthYearIso(iso: string): string {
  if (!iso) return iso;
  const d = new Date(`${iso}T00:00:00Z`);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString("en-GB", {
    month: "short",
    year: "numeric",
    timeZone: "UTC",
  });
}

export default function RecurrencePreviewLine({
  frequency,
  interval,
  startDate,
  endDate,
}: RecurrencePreviewLineProps) {
  if (!startDate || !endDate) return null;
  // Negative range is meaningless to preview; same-day is a single
  // occurrence and renders normally.
  if (endDate < startDate) return null;

  const count = countRecurrenceOccurrences({
    frequency,
    interval,
    startDate,
    endDate,
  });
  if (count <= 0) return null;
  const label = FREQ_LABEL[frequency];

  return (
    <p
      data-testid="recurrence-preview-line"
      className="text-caption text-slate-500 dark:text-slate-400"
    >
      This will create {count} {label} occurrences from{" "}
      {formatMonthYearIso(startDate)} to {formatMonthYearIso(endDate)}.
    </p>
  );
}
